import { useState } from "react";
import { Link } from "react-router";
import { Stack } from "@mui/material";

import classes from "./AdvancedFacials.module.css";
import { treatmentPages, advancedFacialServices } from "./serviceData";
import ServiceAccordion from "./ServiceAccordion";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
  trackAccordionExpand,
} from "../../utils/tracking";

import heroImage from "../../Assets/advanced-facial-top.jpg";
import room from "../../Assets/ra_room.png";
import glowImage2 from "../../Assets/advanced-facials-bottom.jpg";

const page = treatmentPages.find((p) => p.slug === "advanced-facials");

const findService = (id) => advancedFacialServices.find((s) => s.id === id);

const finderCards = [
  {
    concern: "Pigmentation & Dullness",
    icon: "✨",
    serviceId: "radiance-brightening-facial",
  },
  {
    concern: "Dry & Dehydrated Skin",
    icon: "💧",
    serviceId: "radiance-deep-hydration-facial",
  },
  {
    concern: "Oily & Congested Skin",
    icon: "🎯",
    serviceId: "radiance-pore-refining-facial",
  },
  {
    concern: "Sensitised & Reactive Skin",
    icon: "🌿",
    serviceId: "radiance-calming-facial",
  },
  {
    concern: "Acne & Fine Lines",
    icon: "💡",
    serviceId: "dermalux-skin-renewal",
  },
  {
    concern: "Texture & Peach Fuzz",
    icon: "🪶",
    serviceId: "dermaplaning",
  },
  {
    concern: "Not Sure Where to Start?",
    icon: "💬",
    serviceId: "consultation-treatment",
  },
];

const faqs = [
  {
    q: "Which facial is right for me?",
    a: "It depends on your skin's specific needs — pigmentation, dehydration, congestion, sensitivity and general rejuvenation each call for a different approach. Use the guide above to find a starting point, or book our Consultation & Treatment package and we'll assess your skin and recommend the right facial on the day.",
  },
  {
    q: "How often should I have a facial?",
    a: "For general skin maintenance, every 4–6 weeks is a good rhythm, in step with your skin's natural renewal cycle. If you're addressing a specific concern like pigmentation or congestion, we may recommend a short course of closer sessions before moving to a maintenance schedule.",
  },
  {
    q: "Can I combine a facial with microneedling or a chemical peel?",
    a: "Not on the same day — your skin needs time to recover between more intensive treatments. We'll help you build a treatment plan that spaces these out appropriately during your consultation.",
  },
  {
    q: "Do you offer packages for these facials?",
    a: "Yes — course pricing is available on our most popular treatments. Visit our Packages page for details.",
  },
];

const AdvancedFacialsPage = () => {
  const [expandedService, setExpandedService] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Advanced Facials FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  const handleFinderClick = (serviceId) => {
    setExpandedService(serviceId);
    const node = document.getElementById(serviceId);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>
              Nurse-Led · Tailored Skincare
            </p>
            <h1 className={classes.heroTitle}>Advanced Facials</h1>
            <p className={classes.heroMeta}>
              <span>From £35</span> · 9 treatments to choose from
            </p>
            <p className={classes.heroDescription}>
              A bespoke range of facials addressing pigmentation, hydration,
              congestion, sensitivity and ageing — each one personalised to your
              skin's unique needs by a qualified nurse.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Advanced Facials Hero")}
            >
              Book Now
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Client receiving an advanced facial treatment at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>Every Facial Includes</h2>
            <p className={classes.highlightText}>
              Whichever facial suits your skin, every appointment is built
              around the same relaxing ritual: a{" "}
              <strong>thorough cleanse and steam</strong>,{" "}
              <strong>hot towel wrap</strong>, targeted{" "}
              <strong>exfoliation</strong>, a tailored <strong>mask</strong>,
              and a <strong>chest, neck and facial massage</strong> — with the
              specific actives and techniques chosen around your skin's
              individual concern.
            </p>
          </div>
        </div>
      </section>

      {/* Find your facial */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Find Your Facial</p>
          <h2 className={classes.sectionTitle}>
            Which Facial Is Right For You?
          </h2>
          <p className={classes.sectionDescription}>
            Not sure where to begin? Start with your main skin concern — each
            card jumps straight to the full details below.
          </p>
          <div className={classes.finderGrid}>
            {finderCards.map((card) => {
              const service = findService(card.serviceId);
              if (!service) return null;
              return (
                <button
                  key={card.serviceId}
                  type="button"
                  className={classes.finderCard}
                  onClick={() => handleFinderClick(card.serviceId)}
                >
                  <div className={classes.finderIcon} aria-hidden="true">
                    {card.icon}
                  </div>
                  <p className={classes.finderConcern}>{card.concern}</p>
                  <p className={classes.finderFacial}>{service.name}</p>
                  <span className={classes.finderLink}>See details ↓</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image row */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <div className={classes.imageRow}>
            <img
              className={classes.rowImage}
              src={room}
              alt="Radiance Aesthetics treatment room, where advanced facials are performed"
              loading="lazy"
            />
            <img
              className={classes.rowImage}
              src={glowImage2}
              alt="Client's glowing skin after an advanced facial at Radiance Aesthetics"
              loading="lazy"
              style={{ objectPosition: "center 40%" }}
            />
          </div>
        </div>
      </section>

      {/* Full treatment menu — kept as accordion since each facial's detail is specific */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Full Treatment Menu</p>
          <h2 className={classes.sectionTitle}>Explore Every Facial</h2>
          <p className={classes.menuIntro}>
            Tap a treatment to see its full description, duration and pricing,
            and book it directly.
          </p>
          <Stack className={classes.accordionWrapper} gap={2}>
            {advancedFacialServices.map((service) => (
              <div id={service.id} key={service.id}>
                <ServiceAccordion
                  service={service}
                  isExpanded={expandedService === service.id}
                  onChange={(event, isExpanded) => {
                    if (isExpanded) {
                      trackAccordionExpand(
                        service.name,
                        "Advanced Facials Accordion",
                      );
                    }
                    setExpandedService(isExpanded ? service.id : false);
                  }}
                />
              </div>
            ))}
          </Stack>
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>
                    {faq.q === "Do you offer packages for these facials?" ? (
                      <>
                        Yes — course pricing is available on our most popular
                        treatments. Visit our{" "}
                        <Link to="/packages">Packages page</Link> for details.
                      </>
                    ) : (
                      faq.a
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready For Your Next Facial?</h2>
        <p className={classes.ctaDescription}>
          Book online today, or get in touch for a free consultation to find the
          right facial for your skin.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Advanced Facials Bottom CTA")}
        >
          Book Now
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Advanced Facials" />
      </div>
    </>
  );
};

export default AdvancedFacialsPage;
